import _ from 'lodash-es';
import APIV5 from '~/models/api';
import QueryWhereRule from '~/scripts/services/model/query-where-rule';
import QueryWhereChild from '~/scripts/services/model/query-where-child';
import QueryWhereRules from '~/scripts/services/model/query-where-rules';
import QueryWhereChildren from '~/scripts/services/model/query-where-children';
import QueryWhere from '~/scripts/services/model/query-where';
import QueryWhereNode from '~/scripts/services/model/query-where-node';
// eslint-disable-next-line import/no-cycle
import { operatorsByType, types } from '../query-editor/query-editor.controller';

export default class QueryBuilderChildrenController {
	/* @ngInject */
	/**
	 * @param {import('angular').IRootElementService} $element
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {import('angular').ui.bootstrap.IPositionService} $uibPosition
	 * @param {any} KTranslate
	 */
	constructor(
		$element,
		$injector,
		$scope,
		$timeout,
		$uibPosition,
		KTranslate,
	) {
		this.$element = $element;
		this.$injector = $injector;
		this.$scope = $scope;
		this.$timeout = $timeout;
		this.$uibPosition = $uibPosition;
		this.KTranslate = KTranslate;

		this.conditions = [
			{ name: 'AND' },
			{ name: 'OR' },
		];

		/** @type {any} */
		this.formBuilder = undefined;

		/** @type {?QueryWhereChild} */
		this.group = null;

		/** @type {?import('../query-editor/query-editor.controller').default} */
		this.editor = null;

		/** @type {boolean} */
		this.loadingChildrenNodes = false;

		/** @type {any} */
		this.options = undefined;

		/** @type {?QueryBuilderChildrenController} */
		this.parent = null;

		this.root = undefined;
		this.tab = undefined;

		/** @type {{node: any}} */
		this.tmp = {
			node: undefined,
		};
	}

	/**
	 * @param {QueryWhereChild} group
	 * @param {QueryWhereChild} parent
	 */
	addChildren(group, parent) {
		const child = new QueryWhereChild({
			children: new QueryWhereChildren(),
			node: 'parent',
		}, parent);

		if (!group.children) {
			group.children = new QueryWhereChildren();
		}
		group.children?.items.push(child);
	}

	/**
	 * @param {QueryWhereChild} group
	 * @param {any} item
	 * @param {string} type
	 */
	addNode(group, item, type) {
		if (!type) {
			throw new Error('missing type');
		}
		else if (type !== 'rule' && type !== 'child') {
			throw new Error(`invalid type ${type}`);
		}

		const node = new QueryWhereNode(item);

		if (group.node instanceof QueryWhereNode && group.node.inherits_extended === -1) {
			node.inherits_extended = -1;
		}

		if (type === 'rule') {
			const rule = new QueryWhereRule();
			rule.setNode(node);
			rule.setParent(group);
			// @ts-ignore
			rule.operator = operatorsByType[types[node.type]][1].id;

			// Object.assign(rule.node, node);
			if (!group.rules) {
				group.rules = new QueryWhereRules();
			}
			if (group.rules.items.length === 0) {
				group.rules.condition = 'AND';
			}
			group.rules.items.push(rule);
		}
		else if (type === 'child') {
			const child = new QueryWhereChild();
			child.node = node;
			if (!group.children) {
				group.children = new QueryWhereChildren();
			}
			if (group.children.items.length === 0) {
				group.children.condition = 'AND';
			}
			group.children.items.push(child);
			this.tmp.node = undefined;
		}
	}

	/**
	 * @param {QueryWhereRules | QueryWhere | QueryWhereRule} rules
	 * @param {any} node
	 */
	addRule(rules, node) {
		const rule = new QueryWhereRule(null, node);
		const operators = node ? operatorsByType[types[node.type]] : undefined;
		if (operators) {
			rule.operator = operators[1].id;
			if (rules instanceof QueryWhereRules && rules.items) {
				rules.items.push(rule);
			}
			else if (rules instanceof QueryWhereRule && rules.rules?.items) {
				rules.rules.items.push(rule);
			}
		}
	}

	enableRules() {
		if (!this.group) {
			return;
		}
		this.group.rules = new QueryWhereRules();
		this.group.rules.condition = 'AND';
		this.addRule(this.group.rules, this.group.node);
	}

	async initNode() {
		if (this.group?.node instanceof QueryWhereNode && this.group.node.kid) {
			this.loadingChildrenNodes = true;
			await this.group?.node?.init(APIV5.instance.mfChildren2(
				this.group.node.kid,
				'QUERY',
				{
					parentid: this.parent?.group?.node instanceof QueryWhereNode ? this.parent.group.node.kid : null,
					descendants: this.group.node.inherits_extended === 0,
				},
				{
					id: 'query-builder-children.init-node',
				},
			));
			this.$timeout(() => {
				this.loadingChildrenNodes = false;
			});
		}
	}

	removeChildren() {
		if (this.group?.children?.items && this.group.children.items.length > 0) {
			for (const group of this.group.children.items) {
				this.removeGroup(group, this.group)
			}
		}
	}

	/**
	 * @param {?QueryWhereChild} group
	 * @param {?QueryWhereChild} parent
	 */
	removeGroup(group, parent) {
		if (!group) {
			return;
		}
		group.children = null;
		group.node = null;
		if (parent?.children) {
			_.remove(parent.children.items, item => item.node === null);
			if (parent.children.items.length === 0) {
				parent.children = null;
			}
		}
	}

	/**
	 * @param {QueryWhereChild} group
	 */
	removeRules(group) {
		group.rules = null;
	}

	/**
	 * @param {any} item
	 */
	selectClassToProcess(item) {
		if (this.group?.node instanceof QueryWhereNode) {
			this.group.node.kid = item?.kid ?? 'K_NODE;ANYTHING';
			this.group.node.name = item?.name;
		}
	}

	$doCheck() {
		// automatically adds node if select.from is present
		if (
			!this.parent
			&& this.group
			&& (
				(this.group.node instanceof QueryWhereNode && !this.group.node?.kid)
				|| !this.group.node
			)
			&& (
				(this.editor?.tab && this.editor.tab.query.from?.length > 0)
				|| this.formBuilder?.query.from?.length > 0
			)
		) {
			const from = this.editor?.tab?.query.from ?? this.formBuilder?.query.from;
			this.group.node = from?.length > 0 ? new QueryWhereNode({
				flow:       null,
				kid:        from[0].node,
				linker_kid: '0', // TODO: check it
				name:       from[0].name,
				type:       from[0].type,
			}) : null;
		}
	}

	$onInit() {
		this.$scope.$watch('$ctrl.group', (/** @type {any} */ newValue, /** @type {any} */ oldValue) => {
			if (!_.isEqual(newValue, oldValue) && this.editor?.queryEditorForm) {
				this.editor.queryEditorForm.$setDirty();
			}

			this.tmp.node = this.group?.node instanceof QueryWhereNode
				? (this.group?.node?.kid === 'K_NODE;ANYTHING' ? undefined : this.group?.node?.kid)
				: undefined;
		}, true);
	}
}
